import { FunctionsHttpError, type SupabaseClient } from '@supabase/supabase-js';
import type { MemberKind } from '@shkola/api';

/** Mirrors `supabase/functions/invite-accept/handler.ts` (deployed separately) plus `network`. */
export type InviteErrorCode =
  | 'bad_request'
  | 'invalid_code'
  | 'invalid_email'
  | 'password_too_short'
  | 'password_too_long'
  | 'weak_password'
  | 'email_taken'
  | 'already_linked'
  | 'invalid_session'
  | 'rate_limited'
  | 'server_error'
  | 'network';

const knownErrors: ReadonlySet<string> = new Set<InviteErrorCode>([
  'bad_request',
  'invalid_code',
  'invalid_email',
  'password_too_short',
  'password_too_long',
  'weak_password',
  'email_taken',
  'already_linked',
  'invalid_session',
  'rate_limited',
  'server_error',
]);

export interface InvitePreview {
  school_name: string;
  kind: MemberKind;
}

export interface InviteAccepted extends InvitePreview {
  member_id: string;
}

export type InviteRequest =
  | { action: 'check'; code: string }
  | { action: 'accept'; code: string; email: string; password: string }
  /** links the invite to the account that is signed in now */
  | { action: 'accept'; code: string };

export type InviteResult<T> = { ok: true; data: T } | { ok: false; error: InviteErrorCode };

export function callInvite(
  supabase: SupabaseClient,
  body: { action: 'check'; code: string },
): Promise<InviteResult<InvitePreview>>;
export function callInvite(
  supabase: SupabaseClient,
  body: InviteRequest,
): Promise<InviteResult<InviteAccepted>>;
export async function callInvite(
  supabase: SupabaseClient,
  body: InviteRequest,
): Promise<InviteResult<InvitePreview | InviteAccepted>> {
  const { data, error } = await supabase.functions.invoke('invite-accept', { body });
  if (!error) return { ok: true, data: data as InviteAccepted };
  if (error instanceof FunctionsHttpError) {
    const payload = (await (error.context as Response).json().catch(() => ({}))) as {
      error?: unknown;
    };
    const code = typeof payload.error === 'string' ? payload.error : '';
    return { ok: false, error: knownErrors.has(code) ? (code as InviteErrorCode) : 'server_error' };
  }
  return { ok: false, error: 'network' };
}
